import React, { useState, useEffect } from "react";
import { C, T, BG, BO, IS, LS } from "../constants/baziData";
import { calcBazi, calcScore } from "../utils/baziCalc";
import { API } from "../constants/config";
import ReportView from "./ReportView";
import ProfileSelector from "../components/ProfileSelector";  // Phase 104

// ── Audit page ────────────────────────────────────────────────────────────────
function AuditPage({lang="en",user,setPage}){
  const profile=JSON.parse(localStorage.getItem("lumina_profile")||"{}");
  // Persist profile fields across sessions
  const savedProfile=JSON.parse(localStorage.getItem("lumina_profile")||"{}");
  const [f,setF]=useState({
    name:profile.full_name||user?.full_name||user?.email?.split("@")[0]||"",
    year:profile.birth_year||1986,month:profile.birth_month||3,
    day:profile.birth_day||14,hour:profile.birth_hour||6,minute:profile.birth_minute||0,
    location:profile.birth_location||"",concern:profile.primary_concern||"",
    gender:profile.gender||"M",
    occupation: savedProfile.occupation || profile.occupation || "",
    life_concerns: savedProfile.life_concerns || profile.life_concerns || "",
  });
  const [result,setResult]=useState(null);const [load,setLoad]=useState(false);
  const [vajraSyncHint,setVajraSyncHint]=useState(false);
  const [feedbackCount,setFeedbackCount]=useState(0);
  useEffect(()=>{
    const token=localStorage.getItem("token");
    if(!token)return;
    fetch(`${API}/audit/feedback`,{headers:{Authorization:`Bearer ${token}`}})
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if(d?.count) setFeedbackCount(d.count); })
      .catch(()=>{});
  },[]);
  // Auto-save occupation + life_concerns to localStorage
  useEffect(()=>{
    const prev=JSON.parse(localStorage.getItem("lumina_profile")||"{}");
    if(f.occupation!==(prev.occupation||"")||f.life_concerns!==(prev.life_concerns||"")){
      localStorage.setItem("lumina_profile",JSON.stringify({...prev,occupation:f.occupation,life_concerns:f.life_concerns}));
    }
  },[f.occupation,f.life_concerns]);
  const set=k=>e=>{
    let v=e.target.value;
    if(k==="hour"){v=v==="-1"?-1:(v===""?"":Math.min(23,Math.max(0,parseInt(v,10)||0)));}
    if(k==="minute"){v=v===""?"":Math.min(59,Math.max(0,parseInt(v,10)||0));}
    setF({...f,[k]:v});
  };
  // Phase 35: optional 特定问题 (max 3) — used only when downloading Pro PDF
  const [qs,setQs]=useState({q1:"",q2:"",q3:""});
  const setQ=k=>e=>setQs({...qs,[k]:e.target.value});
  // Phase 84: 时辰推测
  const [showHourGuess,setShowHourGuess]=useState(false);
  const [guessAnswers,setGuessAnswers]=useState({action:"",think:"",social:""});
  const HOUR_QUESTIONS = [
    {key:"action", q:"行动风格", opts:["风风火火，说做就做","稳健慢热，按部就班","灵活多变，随性而行"]},
    {key:"think",  q:"思维模式", opts:["直觉灵感型，相信第一感","逻辑分析型，喜欢推敲","务实落地型，看结果说话"]},
    {key:"social", q:"社交偏好", opts:["喜欢主导，带节奏","喜欢配合，跟对人","独来独往，自己待着舒服"]},
  ];
  const guessHour = () => {
    const {action,think,social} = guessAnswers;
    if(action.includes("风风火火")||social.includes("主导")) return 11;
    if(action.includes("稳健")||think.includes("逻辑")) return 7;
    if(think.includes("直觉")||social.includes("独来")) return 21;
    return 11;
  };

  // ── Backend v3 → local sc shape mapper ───────────────────────────────
  // The backend /audit/full-report returns a rich v3 JSON. ReportView
  // currently reads ~25 fields off `sc` (e.g. sc.overall, sc.proto.deity).
  // To avoid rewriting ReportView in this stage, we map the backend
  // response into the local sc/pillars shape. Raw backend fields
  // (shishen, domains, deity_recommendations, overall_summary, ziwei,
  // key_years) are preserved on the result for stage 2 consumption.
  const mapBackendToLocalShape = (backend) => {
    const s = backend.energy_score?.scores || {};
    const es = backend.energy_score?.elements || {};
    const pctCN = backend.elements?.percentages || {};
    // Backend uses CN keys (木/火/土/金/水); local sc.pct uses EN keys.
    const pct = {
      Wood:  pctCN["木"] ?? 0,
      Fire:  pctCN["火"] ?? 0,
      Earth: pctCN["土"] ?? 0,
      Metal: pctCN["金"] ?? 0,
      Water: pctCN["水"] ?? 0,
    };
    const dominantCN  = es.dominant  || "";
    const deficientCN = (es.deficient_elements && es.deficient_elements[0]) || es.deficient || "";
    const CN2EN = { "木":"Wood","火":"Fire","土":"Earth","金":"Metal","水":"Water" };
    const dominant  = CN2EN[dominantCN]  || dominantCN  || "Earth";
    const lacking   = CN2EN[deficientCN] || deficientCN || "Metal";

    // First priority deity from backend recommendations
    const deity1 = (backend.deity_recommendations || [])[0] || {};
    const tierRaw = backend.energy_score?.tier || {};

    return {
      overall:     s.overall_alignment  ?? 50,
      vitality:    s.vitality_index     ?? 50,
      stress:      s.stress_blockage    ?? 50,
      momentum:    s.cycle_momentum     ?? 50,
      relational:  s.relational_field   ?? 50,
      pct,
      dominant,
      lacking,
      tier: {
        color: tierRaw.color || "#C9A96E",
        label: tierRaw.label || "—",
      },
      // proto: legacy field used by DeityCard. Fill from backend deity rec.
      proto: {
        g:      dominant,
        deity:  deity1.deity_en || deity1.deity || "—",
        p:      deity1.practice || "",
        d:      deity1.diet     || "",
      },
    };
  };

  // Remap backend pillars shape to local pillars shape.
  // Local ReportView reads: pillars[p].gz / .en / .se / .be and pillars.day.gz[0]
  // Backend returns:         pillars[p].ganzhi / .en / .stem_element / .branch_element
  const mapBackendPillars = (backendPillars) => {
    if (!backendPillars) return null;
    const out = {};
    for (const p of ["year","month","day","hour"]) {
      const bp = backendPillars[p] || {};
      out[p] = {
        gz: bp.ganzhi || "",
        en: bp.en     || "",
        se: bp.stem_element   || "",
        be: bp.branch_element || "",
      };
    }
    return out;
  };

  const go = async () => {
    const h=parseInt(f.hour,10);
    if(isNaN(h)||h<-1||h>23){
      alert(lang==="zh"?"出生时辰请输入 0–23，或选择「未知」":"Birth hour must be 0–23, or select Unknown");
      return;
    }
    const m=parseInt(f.minute??0,10);
    if(isNaN(m)||m<0||m>59){
      alert(lang==="zh"?"出生分钟请输入 0–59":"Birth minute must be 0–59");
      return;
    }
    setLoad(true);
    const isPremium = user && ["pro","L1","L2","L3","L4"].includes(user.tier);

    const buildLocal = () => {
      const pillars = calcBazi(+f.year, +f.month, +f.day, +f.hour);
      const sc = calcScore(pillars);
      const ts = new Date();
      const rid = `LMN-${f.year}${String(f.month).padStart(2,"0")}${String(f.day).padStart(2,"0")}-${ts.toISOString().slice(11,19).replace(/:/g,"")}`;
      return {
        pillars, sc,
        name: f.name, location: f.location, concern: f.concern,
        gender: f.gender, ts, rid,
        _source: "local",
        // Phase 35: also expose _form on local path so Pro download works
        _form: { year: +f.year, month: +f.month, day: +f.day, hour: +f.hour, minute: +(f.minute??0),
                 name: f.name, location: f.location||"", concern: f.concern||"",
                 gender: f.gender||"M", lang,
                 questions: [qs.q1, qs.q2, qs.q3].map(s=>s.trim()).filter(Boolean) },
      };
    };

    const saveToHistory = (r) => {
      // Preserve existing behavior: write to localStorage history.
      const hist = JSON.parse(localStorage.getItem("lumina_history") || "[]");
      hist.unshift({
        ...r,
        ts: (r.ts instanceof Date ? r.ts : new Date(r.ts || Date.now())).toISOString(),
        name:       f.name,
        birthYear:  +f.year,
        birthMonth: +f.month,
        birthDay:   +f.day,
        birthHour:  +f.hour,
        gender:     f.gender || "M",
        city:       f.location || "",
      });
      localStorage.setItem("lumina_history", JSON.stringify(hist.slice(0, 20)));
      // Phase 58: persist birth data for DailyEnergyWidget
      localStorage.setItem("lumina_last_birth", JSON.stringify({
        year: +f.year, month: +f.month, day: +f.day,
        hour: +f.hour, gender: (f.gender || "M").toLowerCase(),
      }));
    };

    const finish = (r) => {
      saveToHistory(r);
      setResult({ ...r, birthYear: +f.year });
      setLoad(false);
    };

    if (isPremium) {
      try {
        const token = localStorage.getItem("lumina_token");
        const res = await fetch(`${API}/audit/full-report-pro/json`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type":  "application/json",
          },
          body: JSON.stringify({
            name: f.name,
            birth_year: +f.year,
            birth_month: +f.month,
            birth_day: +f.day,
            birth_hour: +f.hour,
            gender: f.gender === "F" ? "female" : "male",
            language: lang || "zh",
            include_ziwei: true,
            occupation: f.occupation || undefined,
            life_concerns: f.life_concerns || undefined,
            questions: [qs.q1, qs.q2, qs.q3].map(s=>s.trim()).filter(Boolean),
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const backend = data.report;
        backend._report_number = data.report_number;
        backend._user_tier = data.user_tier || "";
        backend._credentials = data.credentials || [];
        backend._upcoming_events = data.upcoming_events || [];
        backend.qa_answers = data.qa_answers || [];
        backend._energy_match = data.energy_match || null;

        // Map backend shape → local sc/pillars shape so ReportView works unchanged.
        // Keep the full backend payload alongside for stage 2 consumption.
        const mergedResult = {
          // ReportView-compatible fields
          pillars: mapBackendPillars(backend.pillars),
          sc:      mapBackendToLocalShape(backend),
          name:    f.name,
          location: f.location,
          concern:  f.concern,
          gender:   f.gender || "M",
          ts:       new Date(backend.generated || Date.now()),
          rid:      backend.report_number || backend.meta?.report_id || `CG-${f.year}${String(f.month).padStart(2,"0")}${String(f.day).padStart(2,"0")}`,
          // Metadata
          _source:  "backend",
          // Raw backend payload preserved for stage 2 UI
          _backend: backend,
          _form: { year: +f.year, month: +f.month, day: +f.day, hour: +f.hour, minute: +(f.minute??0),
                   name: f.name, location: f.location||"", concern: f.concern||"",
                   gender: f.gender||"M", lang,
                   // Phase 35: pass optional questions for Pro PDF
                   questions: [qs.q1, qs.q2, qs.q3].map(s=>s.trim()).filter(Boolean) },
        };
        finish(mergedResult);
        // Defect-2: notify Vajra portal users that energy insight is now synced
        if (localStorage.getItem("vajra_token")) {
          setVajraSyncHint(true);
          setTimeout(() => setVajraSyncHint(false), 5000);
        }
        return;
      } catch (err) {
        console.warn("[LUMINA] /audit/full-report failed, falling back to local:", err);
        // fall through to local
      }
    }

    // L0 path, or L1+ fallback after backend error.
    setTimeout(() => {
      finish(buildLocal());
    }, 700);
  };

  if(result)return (
    <>
      {vajraSyncHint && (
        <div style={{position:"fixed",bottom:20,right:20,zIndex:9999,
          background:"rgba(212,175,55,0.92)",color:"#1a0a0a",
          borderRadius:8,padding:"10px 16px",fontSize:12,letterSpacing:.5,
          boxShadow:"0 4px 16px rgba(0,0,0,0.3)",pointerEvents:"none"}}>
          ☸ 能量报告已更新 · 金刚社群建议将同步
        </div>
      )}
      <ReportView result={result} onReset={()=>setResult(null)} birthYear={result.birthYear} lang={lang} API={API} setPage={setPage} user={user}/>
      <VajraBridgeNotice result={result} lang={lang}/>
      {result._source === "local" && <LockedPreview lang={lang} setPage={setPage} user={user} />}
      {/* Phase 102: 订阅入口 — 所有报告完成后推荐 */}
      {!user?.tier || user.tier === "free" || user.tier === "L0" ? (
        <div style={{
          marginTop: 24,
          background: "linear-gradient(135deg, rgba(212,175,55,0.08), rgba(139,105,20,0.05))",
          border: "1px solid rgba(212,175,55,0.25)",
          borderRadius: 14,
          padding: "20px 24px",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 11, color: "#D4AF37", letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>
            {lang === "zh" ? "澄光 Pro" : "LUMINA Pro"}
          </div>
          <div style={{ fontSize: 15, color: "#1A202C", fontWeight: 600, marginBottom: 6 }}>
            {lang === "zh" ? "看完了命盘，想知道前面有什么在等你？" : "Saw your chart. Want to know what's ahead?"}
          </div>
          <div style={{ fontSize: 13, color: "#4A5568", marginBottom: 16, lineHeight: 1.6 }}>
            {lang === "zh"
              ? "澄光Pro $49.99/月 · 每周能量地图 · 月前顾 · 30次问事 · 7天免费试用"
              : "Pro $49.99/mo · Weekly energy map · Monthly outlook · 30 queries · 7-day trial"}
          </div>
          <button onClick={() => setPage("pricing")} style={{
            padding: "10px 32px",
            background: "linear-gradient(135deg, #D4AF37, #B8940A)",
            border: "none", borderRadius: 8,
            color: "#fff", fontSize: 13, fontWeight: 600,
            letterSpacing: 1, cursor: "pointer",
          }}>
            {lang === "zh" ? "了解澄光Pro →" : "See Pro Plans →"}
          </button>
        </div>
      ) : null}
    </>
  );

  return(
    <div style={{
      minHeight:"calc(100vh - 60px)",
      padding:"52px 20px 80px",
      background:"radial-gradient(ellipse 90% 60% at 50% 0%,rgba(224,185,30,.10) 0%,rgba(180,100,20,.04) 50%,transparent 68%)",
    }}>
    <div style={{maxWidth:860,margin:"0 auto"}}>
      {!user&&(
        <div style={{
          background:"rgba(212,184,74,.08)",border:"1px solid rgba(212,184,74,.22)",
          borderRadius:12,padding:"14px 20px",marginBottom:24,
          display:"flex",justifyContent:"space-between",alignItems:"center",
        }}>
          <div style={{fontSize:14,color:C.gold,letterSpacing:".02em"}}>{T[lang].signInBanner}</div>
          <button style={BO} onClick={()=>setPage("register")}>{T[lang].register} →</button>
        </div>
      )}
      <div style={{
        background:"rgba(255,255,255,.72)",
        backdropFilter:"blur(28px) saturate(1.5)",
        WebkitBackdropFilter:"blur(28px) saturate(1.5)",
        border:"1px solid rgba(212,175,55,.20)",
        borderRadius:16,padding:"36px 40px",
        boxShadow:"0 32px 80px rgba(0,0,0,.25),0 0 60px rgba(224,185,30,.05)",
      }}>
        <div style={{marginBottom:28}}>
          <div style={{fontSize:11,letterSpacing:".45em",color:"rgba(212,184,74,.5)",
            textTransform:"uppercase",marginBottom:12,
            fontFamily:"'Inter','Montserrat',sans-serif",fontStyle:"italic"}}>
            {lang==="zh"?"能量审计系统":"Energy Audit System"}
          </div>
          <div style={{fontSize:26,color:C.gold,fontFamily:"'Inter','Montserrat',sans-serif",
            fontWeight:300,letterSpacing:".06em",marginBottom:8}}>{T[lang].generateTitle}</div>
          <div style={{fontSize:14,color:C.mist3,letterSpacing:".02em"}}>{T[lang].generateSub}</div>
        </div>
        {/* Phase 104: Profile selector */}
        {user && <ProfileSelector onSelect={p => {
          setF({...f, name:p.name||f.name, year:p.birth_year, month:p.birth_month,
            day:p.birth_day, hour:p.birth_hour>=0?p.birth_hour:f.hour,
            location:p.birth_location||f.location, gender:p.gender==="male"?"M":"F"});
        }} lang={lang} />}
        <div style={{maxWidth:520,display:"flex",flexDirection:"column",gap:13}}>
          <div><span style={LS}>{T[lang].yourName}</span><input style={IS} value={f.name} onChange={set("name")} placeholder={lang==="zh"?"请输入姓名":"Full name"}/></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:10}}>
            {[[T[lang].year,"year",1900,2024],[T[lang].month,"month",1,12],[T[lang].day,"day",1,31]].map(([l,k,mn,mx])=>(
              <div key={k}><span style={LS}>{l}</span><input style={IS} type="number" min={mn} max={mx} value={f[k]} onChange={set(k)}/></div>
            ))}
            <div><span style={LS}>{T[lang].hour}</span>
              <select style={IS} value={f.hour} onChange={set("hour")}>
                <option value="-1">{lang==="zh"?"未知":"Unknown"}</option>
                {Array.from({length:24},(_,i)=>(
                  <option key={i} value={i}>{i}:00</option>
                ))}
              </select>
            </div>
            <div><span style={LS}>{T[lang].minute}</span><input style={IS} type="number" min={0} max={59} value={f.minute} onChange={set("minute")}/></div>
          </div>
          {/* Phase 84: 推测时辰 */}
          {f.hour === -1 && (
            <div style={{background:"rgba(212,175,55,0.06)",border:"1px solid rgba(212,175,55,0.2)",borderRadius:8,padding:"12px 16px"}}>
              <div style={{fontSize:13,color:"#A0813C",fontWeight:600,marginBottom:8,cursor:"pointer"}}
                   onClick={()=>setShowHourGuess(!showHourGuess)}>
                {lang==="zh"?"🔍 帮我推测时辰":"🔍 Help me guess my birth hour"} {showHourGuess?"▲":"▼"}
              </div>
              {showHourGuess && (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {HOUR_QUESTIONS.map(({key,q,opts})=>(
                    <div key={key}>
                      <div style={{fontSize:12,color:"#666",marginBottom:4}}>{q}</div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {opts.map(o=>(
                          <span key={o} onClick={()=>setGuessAnswers({...guessAnswers,[key]:o})}
                            style={{fontSize:11,padding:"3px 10px",borderRadius:12,cursor:"pointer",
                              background:guessAnswers[key]===o?"rgba(212,175,55,0.2)":"rgba(0,0,0,0.04)",
                              border:`1px solid ${guessAnswers[key]===o?"#D4AF37":"rgba(0,0,0,0.08)"}`,
                              color:guessAnswers[key]===o?"#A0813C":"#555"}}>
                            {o}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {guessAnswers.action && guessAnswers.think && guessAnswers.social && (
                    <div style={{marginTop:4}}>
                      <button onClick={()=>{setF({...f,hour:guessHour()});setShowHourGuess(false);}}
                        style={{fontSize:12,padding:"5px 16px",borderRadius:6,border:"1px solid #D4AF37",
                          background:"rgba(212,175,55,0.1)",color:"#A0813C",cursor:"pointer"}}>
                        {lang==="zh"?`推测结果: ${guessHour()}:00 · 点击应用`:`Apply guessed hour: ${guessHour()}:00`}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {!showHourGuess && (
                <div style={{fontSize:11,color:"#888",marginTop:4}}>
                  {lang==="zh"
                    ?"未提供时辰时，报告以六字为准（年/月/日三柱），时柱用虚拟占位。穷通宝鉴调候不需要时辰，核心结论依然精准。"
                    :"Without birth hour, the report uses 6-characters (year/month/day). Tiaohou analysis does not require hour — core insights remain accurate."}
                </div>
              )}
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div><span style={LS}>{T[lang].gender}</span>
              <select style={IS} value={f.gender} onChange={set("gender")}>
                <option value="M">{T[lang].male}</option><option value="F">{T[lang].female}</option>
              </select>
            </div>
            <div><span style={LS}>{T[lang].birthLocation}</span>
              <input style={IS} value={f.location} onChange={set("location")} placeholder={T[lang].locationPlaceholder} list="city-list" autoComplete="off"/>
              <datalist id="city-list">
                {[
                  // 中国大陆
                  "北京","上海","广州","深圳","成都","重庆","杭州","南京","武汉","西安",
                  "沈阳","哈尔滨","长春","济南","郑州","合肥","长沙","南昌","福州","厦门",
                  "昆明","贵阳","南宁","海口","石家庄","太原","呼和浩特","银川","西宁","兰州","乌鲁木齐","拉萨",
                  // 港澳台
                  "香港","澳门","台北","台中","高雄",
                  // 东亚（中英双条目，自动补全更友好）
                  "Tokyo","东京","Osaka","大阪","Seoul","首尔","Singapore","新加坡",
                  "Kuala Lumpur","吉隆坡","Bangkok","曼谷","Jakarta","雅加达","Manila","马尼拉",
                  "Ho Chi Minh","胡志明","Hanoi","河内",
                  // 南亚/中东/欧洲
                  "Mumbai","孟买","Delhi","新德里","Kathmandu","加德满都","Dubai","迪拜",
                  "London","伦敦","Paris","巴黎","Amsterdam","阿姆斯特丹","Berlin","柏林","Zurich","苏黎世",
                  "Sydney","悉尼","Melbourne","墨尔本",
                  // 北美（中英双条目）
                  "Toronto","多伦多","Vancouver","温哥华","Montreal","蒙特利尔","Calgary","卡尔加里",
                  "New York","纽约","Los Angeles","洛杉矶","San Francisco","旧金山","Seattle","西雅图",
                  "Chicago","芝加哥","Houston","休斯顿","Miami","迈阿密","Boston","波士顿",
                  "Washington","华盛顿","San Jose","圣何塞","Austin","奥斯汀",
                  "Dallas","达拉斯","Atlanta","亚特兰大","Phoenix","凤凰城","Las Vegas","拉斯维加斯",
                  "Honolulu","檀香山",
                  // 南美
                  "Sao Paulo","圣保罗","Buenos Aires","布宜诺斯艾利斯",
                ].map(c=>(
                  <option key={c} value={c}/>
                ))}
              </datalist>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div><span style={LS}>{T[lang].occupation||"职业（可选）"}</span>
              <input style={IS} value={f.occupation} onChange={set("occupation")}
                placeholder={lang==="zh"?"例：软件工程师":"e.g. Software Engineer"}/></div>
            <div><span style={LS}>{T[lang].lifeConcerns||"当前关切（可选）"}</span>
              <input style={IS} value={f.life_concerns} onChange={set("life_concerns")}
                placeholder={lang==="zh"?"例：孩子升学、职业转型":"e.g. kids' education, career change"}/></div>
          </div>

          {/* Phase 35: optional 特定问题 — surfaced in Pro PDF Chapter 5 */}
          <div style={{
            marginTop:6,paddingTop:14,borderTop:"1px dashed rgba(212,175,55,.25)",
            display:"flex",flexDirection:"column",gap:8,
          }}>
            <div style={{fontSize:12,letterSpacing:".08em",color:"rgba(212,184,74,.7)",
              fontFamily:"'Inter','Montserrat',sans-serif",fontStyle:"italic",
              textTransform:"uppercase",marginBottom:2}}>
              {lang==="zh"?"特定问题（可选 · 最多 3 题）":"Specific Questions (optional · up to 3)"}
            </div>
            <input style={IS} value={qs.q1} onChange={setQ("q1")}
              placeholder={lang==="zh"?"例：我的商业贷款今年能批下来吗？":"e.g. Will my business loan be approved this year?"}/>
            <input style={IS} value={qs.q2} onChange={setQ("q2")}
              placeholder={lang==="zh"?"例：今年是否适合换工作？":"e.g. Is this year suitable for changing jobs?"}/>
            <input style={IS} value={qs.q3} onChange={setQ("q3")}
              placeholder={lang==="zh"?"例：和某人的合作是否合适？":"e.g. Is the partnership with X suitable?"}/>
            <div style={{fontSize:11,color:C.mist3,letterSpacing:".02em"}}>
              {lang==="zh"
                ? "提交后将在「澄光完整版」PDF 第 5 章中解析（需 L1+ 订阅或 $149 30 天访问）。"
                : "Answers appear in Chapter 5 of the LUMINAARC Pro PDF (L1+ or $149 30-day access)."}
            </div>
          </div>

          <button style={{...BG,opacity:load||!f.name?0.6:1}} onClick={go} disabled={load||!f.name}>{load?T[lang].computing:T[lang].generateBtn}</button>
          {load && (
            <div style={{textAlign:'center',padding:'12px 0',color:'#888',fontSize:13}}>
              <div style={{marginBottom:4}}>⏳ AI 正在解读您的命盘...</div>
              {feedbackCount>0 && (
                <div style={{color:'#8B6914',fontSize:12}}>
                  💡 您之前的 {feedbackCount} 次反馈已被本次解读引用
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}

export default AuditPage;

// ─── L0 锁定预览 ──────────────────────────────────────────────────────────────
function LockedPreview({ lang, setPage, user }) {
  const zh = lang === "zh";
  const items = zh ? [
    { icon: "🌟", label: "十神性格深度分析", desc: "食神·伤官·正财·偏财…你的性格底色与核心动能" },
    { icon: "📅", label: "大运流转全分析", desc: "当前大运·未来大运·关键转折年份" },
    { icon: "🌙", label: "12个月月运详解", desc: "逐月能量变化·决策窗口·注意事项" },
    { icon: "🏰", label: "紫微斗数十二宫", desc: "命宫·夫妻宫·财帛宫·官禄宫完整解读" },
    { icon: "💡", label: "修行建议与能量调和", desc: "基于强弱喜忌的实修指引" },
  ] : [
    { icon: "🌟", label: "Ten Gods Personality", desc: "Your core drives and behavioral patterns decoded" },
    { icon: "📅", label: "Full Luck Cycles", desc: "Current & future decade cycles with key turning years" },
    { icon: "🌙", label: "12-Month Fortune Detail", desc: "Monthly energy shifts, decision windows, cautions" },
    { icon: "🏰", label: "Ziwei 12 Palaces", desc: "Full Destiny, Spouse, Wealth, Career palace analysis" },
    { icon: "💡", label: "Practice Guidance", desc: "Energy harmony & cultivation direction based on your chart" },
  ];

  return (
    <div style={{
      maxWidth: 860, margin: "32px auto 0", padding: "0 20px",
    }}>
      <div style={{
        background: "rgba(255,255,255,.78)", backdropFilter: "blur(12px)",
        border: "1px solid rgba(212,175,55,.25)", borderRadius: 14,
        padding: "24px 28px",
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#8B6914", marginBottom: 6, letterSpacing: ".04em" }}>
          {zh ? "🔓 以上为基础排盘 · 解锁完整解读" : "🔓 You're viewing the basic chart — unlock the full reading"}
        </div>
        <p style={{ fontSize: 13, color: "#64748B", lineHeight: 1.7, marginBottom: 18 }}>
          {zh
            ? "完整报告包含以下章节，由八字+紫微双引擎深度计算，而非AI凭空生成。"
            : "The full report includes the sections below — all computed by our Bazi + Ziwei dual engine, not AI hallucination."}
        </p>

        {/* Locked items */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {items.map((item, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "flex-start", gap: 12,
              padding: "12px 14px",
              background: "rgba(249,247,242,.6)",
              borderRadius: 10,
              border: "1px solid rgba(212,175,55,.12)",
            }}>
              <span style={{ fontSize: 20, flexShrink: 0, filter: "grayscale(100%)", opacity: .6 }}>{item.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 2 }}>
                  {item.label}
                  <span style={{
                    fontSize: 10, marginLeft: 8, padding: "2px 8px",
                    background: "rgba(212,175,55,.12)", color: "#8B6914",
                    borderRadius: 6, fontWeight: 600,
                  }}>🔒 L1</span>
                </div>
                <div style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.5 }}>
                  {item.desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <button
            onClick={() => user ? setPage("pricing") : setPage("register")}
            style={{
              background: "linear-gradient(135deg,#D4AF37,#064E3B)", color: "#fff",
              border: "none", borderRadius: 10, padding: "12px 28px",
              fontSize: 14, fontWeight: 600, cursor: "pointer",
              fontFamily: "inherit", letterSpacing: ".03em",
            }}
          >
            {zh ? "解锁完整报告 · $49 起" : "Unlock Full Report · from $49"}
          </button>
          <span style={{ fontSize: 12, color: "#94A3B8" }}>
            {zh ? "一次付款·永久保留·可随时升级订阅" : "One-time · Keep forever · Upgrade anytime"}
          </span>
        </div>
      </div>
    </div>
  );
}

// Phase 66: Vajra bridge notice shown after pro report generation
const TIER_LEVELS = { L1:[1,2], L2:[1,2,3], L3:[1,2,3,4], L4:[1,2,3,4,5] };

function VajraBridgeNotice({ result, lang }) {
  const reportNum = result?._backend?._report_number || result?._backend?.meta?.report_id;
  const tier = (result?._backend?._user_tier || "").toUpperCase();
  const levels = TIER_LEVELS[tier];
  if (!reportNum || !levels) return null;

  return (
    <div style={{
      maxWidth: 860, margin: "0 auto", padding: "0 20px 32px",
    }}>
      <div style={{
        background: "rgba(6,78,59,0.04)", border: "1px solid rgba(6,78,59,0.12)",
        borderRadius: 10, padding: "14px 18px",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#064E3B", marginBottom: 4 }}>
            🔓 {lang === "zh" ? "法教权限提示" : "Dharma Access Notice"}
          </div>
          <div style={{ fontSize: 12, color: "#4a3a1e", lineHeight: 1.7 }}>
            {lang === "zh"
              ? `你的 ${tier} 报告（编号 ${reportNum}）可解锁 Vajra Portal 法教资料 Level ${levels[0]}-${levels[levels.length-1]}。登录 Vajra 后自动生效，或在「设置 → 命理报告激活」手动输入报告编号。`
              : `Your ${tier} report (${reportNum}) unlocks Vajra dharma resources Level ${levels[0]}-${levels[levels.length-1]}. Auto-activated if your emails match, or enter your report number in Vajra Settings.`}
          </div>
        </div>
        <a href="/vajra" style={{
          display: "inline-block", padding: "7px 16px", borderRadius: 7, fontSize: 12, fontWeight: 600,
          background: "#064E3B", color: "#fff", textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0,
        }}>
          {lang === "zh" ? "前往 Vajra →" : "Go to Vajra →"}
        </a>
      </div>
    </div>
  );
}
